/*
 * Multiply the displayed product price by the selected quantity.
 *
 * Dawn shows the unit price and never changes it with the quantity stepper,
 * which reads as broken to shoppers who expect a running total.
 *
 * Opt-in: only runs when the product form carries data-price-by-quantity="true"
 * (Theme settings -> Product -> Multiply price by quantity).
 *
 * Reads the unit price from the price element's own markup once, then
 * recalculates locally. On a variant change Dawn re-renders the price element,
 * so the unit price is re-read from the fresh DOM rather than cached.
 */
(function () {
  var MULTIPLIED = 'data-qty-multiplied';
  var TOTAL = 'data-qty-total';
  var OBSERVED = 'data-qty-observed';

  /** Parse a money string ("$19.50", "1.234,56 kr") into an integer of minor units. */
  function parseMoney(text) {
    if (!text) return null;
    var cleaned = text.replace(/[^\d.,-]/g, '').trim();
    if (!cleaned) return null;

    // Whichever separator appears last is the decimal separator.
    var lastDot = cleaned.lastIndexOf('.');
    var lastComma = cleaned.lastIndexOf(',');
    var decimalSep = lastDot > lastComma ? '.' : lastComma > lastDot ? ',' : '';

    var normalised;
    if (decimalSep) {
      var groupSep = decimalSep === '.' ? ',' : '.';
      normalised = cleaned.split(groupSep).join('').replace(decimalSep, '.');
    } else {
      normalised = cleaned;
    }

    var value = parseFloat(normalised);
    return isNaN(value) ? null : Math.round(value * 100);
  }

  /** Render minor units back using the original string's formatting. */
  function formatLike(sample, minorUnits) {
    var major = minorUnits / 100;
    var lastDot = sample.lastIndexOf('.');
    var lastComma = sample.lastIndexOf(',');
    var usesComma = lastComma > lastDot;

    var hasDecimals = /[.,]\d{2}(\D|$)/.test(sample);
    var body = hasDecimals ? major.toFixed(2) : String(Math.round(major));
    if (usesComma) body = body.replace('.', ',');

    // Reuse whatever surrounded the digits, so currency symbols and any suffix
    // (for example a " USD") survive.
    return sample.replace(/[\d.,]+/, body);
  }

  function priceNodes(root) {
    return root.querySelectorAll(
      '.price__regular .price-item--regular, .price__sale .price-item--sale, .eb-sub-price .eb-sub-price__was, .eb-sub-price .eb-sub-price__now'
    );
  }

  function update(form) {
    var container = form.closest('product-info') || document;
    var input = form.querySelector('.quantity__input');
    var priceRoot = container.querySelector('.product__price-block') || container.querySelector('.price');
    if (!input || !priceRoot) return;

    var qty = parseInt(input.value, 10);
    if (!qty || qty < 1) qty = 1;

    priceNodes(priceRoot).forEach(function (node) {
      // First pass for this node: remember the unit price exactly as rendered.
      if (!node.hasAttribute(MULTIPLIED)) {
        node.setAttribute(MULTIPLIED, node.textContent.trim());
      }
      var unitText = node.getAttribute(MULTIPLIED);
      var unit = parseMoney(unitText);
      if (unit === null) return;

      var text = qty > 1 ? formatLike(unitText, unit * qty) : unitText;
      node.textContent = text;
      node.setAttribute(TOTAL, text);
    });

    updateSaving(priceRoot, qty);

  }

  /*
   * Seal Subscriptions rewrites the price node client-side on products that
   * require a selling plan, which lands after this script has multiplied and
   * silently restores a single unit's price. The quantity stepper then reads
   * as broken: "Minimum of 2" above a one-bag price.
   *
   * Seal always writes a unit price, and we always write a total, so the two
   * are told apart by what we last wrote. Anything that differs is taken as a
   * fresh unit price rather than as damage to undo, which matters on
   * discounted plans: Seal's figure is the one that should be multiplied.
   */
  function reconcile(form, priceRoot) {
    var refreshed = false;

    priceNodes(priceRoot).forEach(function (node) {
      var ours = node.getAttribute(TOTAL);
      if (ours === null) return;

      var current = node.textContent.trim();
      if (current === ours) return;

      node.setAttribute(MULTIPLIED, current);
      refreshed = true;
    });

    if (refreshed) update(form);
  }

  function watch(form) {
    var container = form.closest('product-info') || document;
    var priceRoot = container.querySelector('.product__price-block') || container.querySelector('.price');
    if (!priceRoot || priceRoot.hasAttribute(OBSERVED)) return;

    priceRoot.setAttribute(OBSERVED, '');
    new MutationObserver(function () {
      reconcile(form, priceRoot);
    }).observe(priceRoot, { subtree: true, childList: true, characterData: true });
  }

  /*
   * Products where a subscription is optional open on the one-time price, and the
   * saving only appears once the customer chooses Subscribe - a client-side change
   * made by the app. The regular price is known server-side, so comparing it with
   * whatever is currently displayed says whether a saving is active, without this
   * needing to recognise the app's markup or listen for its events.
   */
  function updateSaving(priceRoot, qty) {
    var was = priceRoot.querySelector('[data-sub-was]');
    var tag = priceRoot.querySelector('[data-sub-tag]');
    if (!was || !tag) return;

    // Carried on the element itself, so it is re-rendered with the variant.
    var regular = parseInt(was.getAttribute('data-regular-price'), 10);
    if (!regular) return;

    var node = priceRoot.querySelector('[' + MULTIPLIED + ']');
    var unitText = node && node.getAttribute(MULTIPLIED);
    var unit = parseMoney(unitText);

    // No saving on display: either nothing is chosen yet, or the chosen plan
    // costs the same. Either way there is nothing to strike through.
    if (unit === null || unit >= regular) {
      was.hidden = true;
      tag.hidden = true;
      return;
    }

    was.textContent = formatLike(unitText, regular * qty);
    tag.textContent = Math.round(((regular - unit) / regular) * 100) + '% off';
    was.hidden = false;
    tag.hidden = false;
  }

  function bind(form) {
    if (form.dataset.priceByQuantity !== 'true') return;
    var input = form.querySelector('.quantity__input');
    if (!input) return;

    ['change', 'input'].forEach(function (evt) {
      input.addEventListener(evt, function () {
        update(form);
      });
    });

    update(form);
    watch(form);
  }

  function init() {
    document.querySelectorAll('[data-price-by-quantity]').forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', init);

  // Dawn replaces the price and quantity markup on a variant change, which
  // drops our listeners and restores the unit price. Re-bind when that happens.
  document.addEventListener('variant:change', init);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(init);
    });
  }
})();
